import { AgendaAppointment } from '../types/agenda.types';
import { WeekBlock, EWeekBlockStatus } from '../types/agenda.types';
import { EAppointmentStateValues, EAppointmentState } from '../../../enums/EAppointmentState';

/** Alto de una hora en la grilla semanal (WeekScheduleView lo usa para dibujar las filas). */
export const PX_PER_HOUR = 100;
const DEFAULT_BLOCK_MINUTES = 30;

function mapStateToBlockStatus(stateId: number | null): EWeekBlockStatus {
  if (!stateId) return EWeekBlockStatus.SCHEDULED;

  if (stateId === EAppointmentStateValues[EAppointmentState.SCHEDULLED]) return EWeekBlockStatus.SCHEDULED;
  if (stateId === EAppointmentStateValues[EAppointmentState.CONFIRMED]) return EWeekBlockStatus.CONFIRMED;
  if (stateId === EAppointmentStateValues[EAppointmentState.INQUEUE] ||
      stateId === EAppointmentStateValues[EAppointmentState.PENDING_ATTENTION]) return EWeekBlockStatus.WAITING;
  if (stateId === EAppointmentStateValues[EAppointmentState.INATTENTION] ||
      stateId === EAppointmentStateValues[EAppointmentState.IN_OBSERVATION]) return EWeekBlockStatus.IN_ATTENTION;
  if (stateId === EAppointmentStateValues[EAppointmentState.COMPLETED]) return EWeekBlockStatus.COMPLETED;
  if (stateId === EAppointmentStateValues[EAppointmentState.MISSED]) return EWeekBlockStatus.MISSED;

  return EWeekBlockStatus.SCHEDULED;
}

/**
 * Converts a list of AgendaAppointment into positioned WeekBlock objects.
 *
 * @param appointments - appointments returned by the API
 * @param weekStartDate - the Monday of the displayed week (Date object)
 * @param hourStart - first visible hour in the grid (default 8)
 */
export function appointmentsToWeekBlocks(
  appointments: AgendaAppointment[],
  weekStartDate: Date,
  hourStart = 8,
): WeekBlock[] {
  const weekStartMs = weekStartDate.getTime();

  return appointments
    .filter((a) => a.appointmentdatetime)
    .map((a) => {
      const dt = new Date(a.appointmentdatetime!);

      // dayIndex: 0 = Monday … 5 = Saturday
      const diffDays = Math.floor((dt.getTime() - weekStartMs) / (1000 * 60 * 60 * 24));
      const dayIndex = Math.max(0, Math.min(5, diffDays));

      // Vertical position
      const hours = dt.getHours();
      const minutes = dt.getMinutes();
      const topPx = (hours - hourStart) * PX_PER_HOUR + (minutes / 60) * PX_PER_HOUR;

      // Duration — derive from appointmentfromtime/appointmenttotime when available
      let durationMinutes = DEFAULT_BLOCK_MINUTES;
      if (a.appointmentfromtime && a.appointmenttotime) {
        const [fh, fm] = a.appointmentfromtime.split(':').map(Number);
        const [th, tm] = a.appointmenttotime.split(':').map(Number);
        const diff = (th * 60 + tm) - (fh * 60 + fm);
        if (diff > 0) durationMinutes = diff;
      }
      const heightPx = Math.max((durationMinutes / 60) * PX_PER_HOUR, 24);

      return {
        dayIndex,
        topPx,
        heightPx,
        label: a.patientname ?? '',
        sublabel: `${String(hours).padStart(2, '0')}:${String(minutes).padStart(2, '0')}`,
        status: mapStateToBlockStatus(a.appointmentstateid),
        appointmentId: a.patientcarequeue ?? undefined,
      };
    });
}
